import {
  isWorkersAiModel,
  modelAllowedFor,
  modelRefusalReason,
  servedOverAiService,
  type AiOperationDefinition,
  type AiOperationId,
  type AiOperationMode,
} from "./operations";

/** A row of D1 `ai_operation_settings`. NULL in a column means the registry default. */
export interface AiOperationSettingsRow {
  operation: string;
  mode: string | null;
  primary_model: string | null;
  fallback_model: string | null;
  updated_by: string;
  updated_at: string;
}

export type AiSettingSource = "default" | "saved";

export interface AiOperationSettingsView {
  id: AiOperationId;
  mode: AiOperationMode;
  primaryModel: string;
  fallbackModel: string | null;
  /** Read-only models tried after the fallback, duplicates of the pair already removed. */
  extraFallbacks: string[];
  source: {
    mode: AiSettingSource;
    primaryModel: AiSettingSource;
    fallbackModel: AiSettingSource;
  };
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface AiSettingsView {
  operations: AiOperationSettingsView[];
  /** Model ids CLIProxyAPI serves now plus `WORKERS_AI_CHAT_MODELS`; null when the list could not be read. */
  servedModels: string[] | null;
  /** Why `servedModels` is null, safe to show staff. */
  servedModelsError: string | null;
}

/** A console write. Omitted = unchanged; `null` = back to the registry default. */
export interface AiOperationUpdate {
  mode?: AiOperationMode | null;
  primaryModel?: string | null;
  fallbackModel?: string | null;
}

export function resolveOperationSetting(
  op: AiOperationDefinition,
  row: AiOperationSettingsRow | null,
): AiOperationSettingsView {
  const savedMode =
    row?.mode != null && (op.modes as readonly string[]).includes(row.mode)
      ? (row.mode as AiOperationMode)
      : null;
  // A stored model the operation may not run (a retired one, GLM on a vision operation, saved before
  // the rule existed or written around the console) reads as unset: the registry default stands in.
  const allowedSaved = (model: string | null | undefined): string | null =>
    op.modelsEditable && model != null && modelAllowedFor(op, model)
      ? model
      : null;
  const savedPrimary = allowedSaved(row?.primary_model);
  const savedFallback = allowedSaved(row?.fallback_model);
  const primaryModel = savedPrimary ?? op.defaultPrimary;
  const fallbackModel = savedFallback ?? op.defaultFallback;
  const taken = new Set<string>([primaryModel]);
  if (fallbackModel) taken.add(fallbackModel);
  const extraFallbacks: string[] = [];
  for (const model of op.extraFallbacks ?? []) {
    if (taken.has(model) || !modelAllowedFor(op, model)) continue;
    taken.add(model);
    extraFallbacks.push(model);
  }
  return {
    id: op.id as AiOperationId,
    mode: savedMode ?? op.defaultMode,
    primaryModel,
    fallbackModel,
    extraFallbacks,
    source: {
      mode: savedMode ? "saved" : "default",
      primaryModel: savedPrimary ? "saved" : "default",
      fallbackModel: savedFallback ? "saved" : "default",
    },
    updatedBy: row?.updated_by ?? null,
    updatedAt: row?.updated_at ?? null,
  };
}

/**
 * The reason a console write must be refused, or null when it may be saved.
 * Pure: the API Worker runs it before the D1 write and the page runs it before
 * the request. `current` is the row as it stands, so primary/fallback can be
 * compared after the update applies.
 */
export function validateOperationUpdate(
  op: AiOperationDefinition,
  update: AiOperationUpdate,
  servedModels: readonly string[] | null,
  current: AiOperationSettingsView | null = null,
): string | null {
  const keys = (["mode", "primaryModel", "fallbackModel"] as const).filter(
    (key) => update[key] !== undefined,
  );
  if (keys.length === 0)
    return "Nothing to save: change the mode or a model first.";

  if (
    update.mode != null &&
    !(op.modes as readonly string[]).includes(update.mode)
  ) {
    return `${op.label} has no mode "${update.mode}". Pick one of: ${op.modes.join(", ")}.`;
  }

  const models = [update.primaryModel, update.fallbackModel].filter(
    (model): model is string => model != null,
  );
  if (models.length > 0) {
    if (!op.modelsEditable)
      return `${op.label} uses a fixed model, so it cannot be changed here.`;
    if (!servedModels) {
      return "The model list could not be read from the AI Gateway, so models cannot be changed now. Reload the page to try again.";
    }
    for (const model of models) {
      // Before the served check: the served list leaves a retired model out, and "retired" is the real answer.
      const refusal = modelRefusalReason(op, model);
      if (refusal) {
        const choices = servedModels.filter(
          (choice) =>
            modelAllowedFor(op, choice) &&
            (!isWorkersAiModel(choice) || servedOverAiService(op)),
        );
        return `${op.label} cannot use "${model}": ${refusal}. Pick one of: ${choices.join(", ")}.`;
      }
      if (!servedModels.includes(model)) {
        return `The AI Gateway does not serve "${model}". Pick a model from the list.`;
      }
      if (isWorkersAiModel(model) && !servedOverAiService(op)) {
        return `${op.label} runs inside the API Worker, which cannot call Workers AI models yet. Pick a model that is not "@cf/…".`;
      }
    }
  }

  // A save that names no model changes no model: a moved default may already have made the two equal.
  if (update.primaryModel === undefined && update.fallbackModel === undefined) {
    return null;
  }
  const primary =
    update.primaryModel === undefined
      ? (current?.primaryModel ?? null)
      : (update.primaryModel ?? op.defaultPrimary);
  const fallback =
    update.fallbackModel === undefined
      ? (current?.fallbackModel ?? null)
      : (update.fallbackModel ?? op.defaultFallback);
  if (primary && fallback && primary === fallback) {
    return "The main and fallback model must be different, so a failure can move to another model.";
  }
  return null;
}
